import React from "react";
import {DateTime} from "luxon";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBinoculars, faLocationDot, faMapLocationDot, faStar} from "@fortawesome/free-solid-svg-icons";
import {Link} from "react-router-dom";
import {buildHotspotDetailPath} from "../../views/hotspot-explorer/helpers";
import {pluralize} from "../../helpers/helpers";
import type {CalendarDayPanelProps} from "./types";
import "./calendar-day-panel.scss";

export default function CalendarDayPanel({day, date, speciesDetailPaths, onOpenDayMap}: CalendarDayPanelProps) {
    const dateLabel = DateTime.fromISO(date).toFormat('cccc, LLLL d, yyyy');

    if (!day) {
        return <aside className="calendar-day-panel" aria-live="polite">
            <p className="calendar-day-panel__eyebrow">Selected day</p>
            <h2>{dateLabel}</h2>
            <p className="calendar-day-panel__empty">No birding activity is recorded for this date.</p>
        </aside>;
    }

    return <aside className="calendar-day-panel" aria-live="polite">
        <p className="calendar-day-panel__eyebrow">Selected day</p>
        <h2>{dateLabel}</h2>
        <div className="calendar-day-panel__stats">
            <span><FontAwesomeIcon icon={faBinoculars} aria-hidden="true" /><strong>{day.speciesCount}</strong> {pluralize(day.speciesCount, "species")}</span>
            <span><FontAwesomeIcon icon={faLocationDot} aria-hidden="true" /><strong>{day.locationCount}</strong> {pluralize(day.locationCount, "location")}</span>
            <span><strong>{day.checklistCount}</strong> {pluralize(day.checklistCount, "checklist")}</span>
            <span><FontAwesomeIcon icon={faStar} aria-hidden="true" /><strong>{day.liferCount}</strong> {pluralize(day.liferCount, "lifer")}</span>
        </div>
        {day.liferSpecies.length ? <section className="calendar-day-panel__lifers" aria-labelledby="calendar-day-lifers">
            <h3 id="calendar-day-lifers"><FontAwesomeIcon icon={faStar} aria-hidden="true" /> Lifers</h3>
            <ul>
                {day.liferSpecies.map(species => <li key={species.taxonomicOrder}>
                    {speciesDetailPaths[species.taxonomicOrder]
                        ? <Link to={speciesDetailPaths[species.taxonomicOrder]}>{species.commonName}</Link>
                        : <strong>{species.commonName}</strong>}
                    <span>{species.scientificName}</span>
                </li>)}
            </ul>
        </section> : null}
        <section className="calendar-day-panel__section">
            <h3>Top species</h3>
            <ol>
                {day.topSpecies.map(species => <li key={species.taxonomicOrder}>
                    {speciesDetailPaths[species.taxonomicOrder]
                        ? <Link to={speciesDetailPaths[species.taxonomicOrder]}>{species.commonName}</Link>
                        : <span>{species.commonName}</span>}
                    <strong>{species.sightings}</strong>
                </li>)}
            </ol>
        </section>
        <section className="calendar-day-panel__section">
            <h3>Checklists</h3>
            <ul>
                {day.checklistLinks.map(checklist => <li key={checklist.submissionId}>
                    <span className="calendar-day-panel__checklist-links">
                        {checklist.locationId ? <Link to={buildHotspotDetailPath(checklist.locationId)}>{checklist.location}</Link> : <span>{checklist.location}</span>}
                        <a href={`https://ebird.org/checklist/${checklist.submissionId}`} target="_blank" rel="noreferrer">eBird checklist</a>
                    </span>
                    <span>{checklist.speciesCount} {pluralize(checklist.speciesCount, "species")}</span>
                </li>)}
            </ul>
        </section>
        <button type="button" className="calendar-day-panel__map-link" onClick={onOpenDayMap}>
            <FontAwesomeIcon icon={faMapLocationDot} aria-hidden="true" /> View this day on the map
        </button>
    </aside>;
}
